export default class LoadMore extends HTMLElement {
  constructor() {
    super();

    this.error = false;
  }

  connectedCallback() {
    this.updatePaginationCount();

    if (
      !this.pagination ||
      !this.list ||
      !this.loadButtonWrapper ||
      !this.loadButton
    )
      return;

    this.pagination.setAttribute("hidden", "true");

    if (this.canLoadMore) {
      this.loadButtonWrapper.removeAttribute("hidden");
      this.loadButton.addEventListener("click", this.doLoadMore);
    }
  }

  get currentUrl() {
    return new URL(window.location);
  }

  get currentPage() {
    const searchParams = this.currentUrl.searchParams;
    const page = searchParams.get("page") ?? "1";
    return Number(page);
  }

  get itemCount() {
    return Number(this.dataset.itemCount);
  }

  get pageSize() {
    return Number(this.dataset.pageSize);
  }

  get canLoadMore() {
    if (!this.pageSize || !this.itemCount) return false;

    return this.itemCount > this.pageSize * this.currentPage;
  }

  get pagination() {
    return this.querySelector('[data-ref="pagination"]');
  }

  get listSelector() {
    return '[data-ref="paginated-list"]';
  }

  get list() {
    return this.querySelector(this.listSelector);
  }

  get countSelector() {
    return '[data-ref="pagination-count"]';
  }

  get countNode() {
    return this.querySelector(this.countSelector);
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get loadButtonWrapper() {
    return this.querySelector('[data-ref="load-button-wrapper"]');
  }

  get loadButton() {
    return this.querySelector('[data-ref="load-button"]');
  }

  get loadingOverlay() {
    return this.querySelector(".c-loading-overlay");
  }

  doLoadMore = () => {
    if (this.loadButton.getAttribute("aria-disabled") === "true") return;

    this.enableLoading();

    const fetchUrl = this.currentUrl;
    fetchUrl.searchParams.set("page", String(this.currentPage + 1));
    fetchUrl.searchParams.set("section_id", this.sectionId);

    fetch(fetchUrl)
      .then((response) => response.text())
      .then((response) => {
        const parser = new DOMParser();

        const responseDoc = parser.parseFromString(response, "text/html");
        const responseListItems = responseDoc?.querySelectorAll(
          `${this.listSelector} > li`,
        );

        // if no list in response, either there's an error or we've reached the end of the paginated list
        // either way, hide the load button
        if (!responseListItems?.length) {
          this.loadButtonWrapper.setAttribute("hidden", "true");
          return;
        }

        // append items to existing list
        for (const item of responseListItems) {
          this.list.appendChild(item);
        }

        // update URL in browser with new `page` param
        const updatedUrl = this.currentUrl;
        updatedUrl.searchParams.set("page", String(this.currentPage + 1));
        window.history.pushState({}, "", updatedUrl);

        // hide load button if no more items to load
        if (!this.canLoadMore)
          this.loadButtonWrapper.setAttribute("hidden", "true");
      })
      .then(() => {
        this.updatePaginationCount();
      })
      .catch((error) => console.error(error))
      .finally(() => this.disableLoading());
  };

  // localize pagination count numbers and update to latest values
  updatePaginationCount() {
    const paginationFirstNode = this.querySelector(
      '[data-ref="pagination-count-first"]',
    );
    const paginationLastNode = this.querySelector(
      '[data-ref="pagination-count-last"]',
    );
    const paginationTotalNode = this.querySelector(
      '[data-ref="pagination-count-total"]',
    );
    const backToLinkNode = this.querySelector(
      '[data-ref="pagination-back-to-link"]',
    );

    const initialOffset = Number(paginationFirstNode.innerText);

    if (paginationFirstNode) {
      this.updatePaginationNumber(paginationFirstNode, initialOffset);
    }

    if (paginationLastNode) {
      this.updatePaginationNumber(
        paginationLastNode,
        Math.min(this.currentPage * this.pageSize, this.itemCount),
      );
    }

    if (paginationTotalNode) {
      this.updatePaginationNumber(paginationTotalNode, this.itemCount);
    }

    if (backToLinkNode && initialOffset > 1 && this.currentPage > 1) {
      backToLinkNode.removeAttribute("hidden");
    }
  }

  updatePaginationNumber(node, value) {
    const localizedValue = new Intl.NumberFormat().format(value);
    node.innerText = localizedValue;
  }

  enableLoading() {
    this.loadButton.setAttribute("aria-disabled", "true");
    this.loadButton.classList.add("loading");
    this.loadingOverlay?.classList.remove("hidden");
  }

  disableLoading() {
    this.loadButton.setAttribute("aria-disabled", "false");
    this.loadButton.classList.remove("loading");
    this.loadingOverlay?.classList.add("hidden");
  }
}
